export interface ParsedSetCookie {
    name: string;
    value: string;
    path?: string;
    maxAge?: number;
    expires?: Date;
    secure?: boolean;
    httpOnly?: boolean;
    sameSite?: 'lax' | 'strict' | 'none';
}

export function isCookieDeletion(parsed: ParsedSetCookie): boolean {
    return parsed.maxAge === 0 || parsed.value === 'deleted';
}

export function parseSetCookieString(cookieString: string): ParsedSetCookie | null {
    const [cookiePart, ...attributeParts] = cookieString.split('; ');
    if (!cookiePart) return null;

    const eqIndex = cookiePart.indexOf('=');
    if (eqIndex <= 0) return null;

    const name = cookiePart.slice(0, eqIndex);
    const value = cookiePart.slice(eqIndex + 1);

    const attributes: Omit<ParsedSetCookie, 'name' | 'value'> = { path: '/' };

    for (const attr of attributeParts) {
        const attrEqIndex = attr.indexOf('=');
        const key = attrEqIndex === -1 ? attr : attr.slice(0, attrEqIndex);
        const val = attrEqIndex === -1 ? undefined : attr.slice(attrEqIndex + 1);
        if (!key) continue;
        const lowerKey = key.toLowerCase();

        if (lowerKey === 'secure') {
            attributes.secure = true;
        } else if (lowerKey === 'httponly') {
            attributes.httpOnly = true;
        } else if (val) {
            if (lowerKey === 'max-age') {
                attributes.maxAge = Number.parseInt(val, 10);
            } else if (lowerKey === 'expires') {
                attributes.expires = new Date(val);
            } else if (lowerKey === 'path') {
                attributes.path = val;
            } else if (lowerKey === 'samesite') {
                attributes.sameSite = val.toLowerCase() as 'lax' | 'strict' | 'none';
            }
        }
    }

    return { name, value, ...attributes };
}

export function getSharedCookieDomain(hostname: string): string | undefined {
    const isLocalhost = hostname === 'localhost' || hostname.startsWith('127.');
    const isIpAddress = /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
    const parts = hostname.split('.');

    if (isLocalhost || isIpAddress || parts.length < 2) {
        return undefined;
    }

    if (parts.length === 2) {
        return `.${hostname}`;
    }

    return `.${parts.slice(1).join('.')}`;
}

export function parseCookieHeader(header: string): Map<string, string> {
    const cookieMap = new Map<string, string>();

    if (!header.trim()) {
        return cookieMap;
    }

    for (const part of header.split(';')) {
        const trimmed = part.trim();
        if (!trimmed) continue;

        const separatorIndex = trimmed.indexOf('=');
        if (separatorIndex === -1) continue;

        const name = trimmed.slice(0, separatorIndex).trim();
        const value = trimmed.slice(separatorIndex + 1).trim();
        if (name) {
            cookieMap.set(name, value);
        }
    }

    return cookieMap;
}

export function decodeCookieValue(value: string): string {
    return decodeURIComponent(value);
}

export function readClientCookie(name: string): string | null {
    if (typeof document === 'undefined') {
        return null;
    }

    const raw = parseCookieHeader(document.cookie).get(name);
    return raw ? decodeCookieValue(raw) : null;
}

export function buildCookieHeader(cookies: Map<string, string>): string {
    return Array.from(cookies.entries())
        .map(([name, value]) => `${name}=${value}`)
        .join('; ');
}

export function setCookieInHeader(existingHeader: string, name: string, value: string): string {
    const cookies = parseCookieHeader(existingHeader);
    cookies.set(name, value);
    return buildCookieHeader(cookies);
}

export function extractSetCookieHeaders(response: Response): string[] {
    if ('getSetCookie' in response.headers && typeof response.headers.getSetCookie === 'function') {
        return (response.headers.getSetCookie as () => string[])();
    }

    const single = response.headers.get('set-cookie');
    return single ? [single] : [];
}

export function mergeSetCookieHeaders(response: Response, setCookieHeaders: string[]): Response {
    if (setCookieHeaders.length === 0) {
        return response;
    }

    const headers = new Headers(response.headers);
    for (const cookie of setCookieHeaders) {
        headers.append('Set-Cookie', cookie);
    }

    return new Response(response.body, {
        status: response.status,
        statusText: response.statusText,
        headers,
    });
}

export function cloneRequestWithCookie(request: Request, cookieHeader: string): Request {
    const headers = new Headers(request.headers);
    headers.set('cookie', cookieHeader);

    const init: RequestInit = {
        method: request.method,
        headers,
        redirect: request.redirect,
        signal: request.signal,
    };

    if (request.method !== 'GET' && request.method !== 'HEAD') {
        init.body = request.body;
    }

    return new Request(request.url, init);
}
